// PACKAGES/ HOOKS
import React, { useEffect, useMemo } from "react"
import { useAppDispatch, useFeatureFlags, useHeightResize } from "hooks"
import { AllowedTo, useAbac } from "react-abac"

// COMPONENTS
import LocationDetails from "./Details/LocationDetails"
import Contracts from "./Contracts/Contract"
import { LocationSidebar, AntdButton } from "components"

import AdditionalDetail from "./AdditionalDetail/AdditionalDetail"
import Escalations from "./Escalations/Escalation"
import LocationTopBar from "./LocationsTopBar"
import Documents from "./Documents/Documents"
import Posts from "./Posts/Posts"

import { getCustomers } from "store/Customers/customers.slice"
import TimeCaptures from "./TimeCapture/TimeCaptures"
import { getPayGroup } from "store/PayGroups/payGroup.slice"
import LocationQualification from "./Qualifications/LocationQualifications"
// import { getBranches } from "store/Branches/branch.slice"
import { LOCATION_SOCKET_EVENTS, ROOM_EVENTS, SOCKET_ROOMS } from "types/webSocket.types"
import { socket } from "config/socket"
import { saveLocationBySockets } from "store/Location/locationDetails.slice"
import { getSDCounties } from "store/StaticData/SDCounties.slice"
import { Permissions } from "config/constants"
import QRCodeTab from "./QRCode/QRCodeTab"
import Incidents from "./Incidents/Incidents"
import { Divider, Segmented } from "antd-v5"
import { FeatureFlagEnums } from "types/featureFlags.type"
import Complaints from "./Complaints/Complaints"
import useSchedulesQueryParams from "hooks/useSchedulesQueryParams"
import { LocationTabs } from "types/schedules.types"
// UTILS/CONSTANTS

const Index: React.FC = () => {
	useHeightResize({ element: "location-segment-body" })
	const dispatch = useAppDispatch()
	const { userHasPermissions } = useAbac()
	const { featureFlagEnabled } = useFeatureFlags()
	//const [value, setValue] = useState<string>("Detail")
	const { selectedLocation = "", tab: paramTab, selectLocation, setSearchParams } = useSchedulesQueryParams()

	//const selectedLocation = useAppSelector(selectSelectedLocation)

	useEffect(() => {
		if (!paramTab || !Object.values(LocationTabs).includes(paramTab as LocationTabs)) {
			setSearchParams({ tab: LocationTabs.Detail })
		}
	}, [paramTab])

	const handleSelected = (location: string) => selectLocation(location)

	useEffect(() => {
		dispatch(getCustomers())
		dispatch(getPayGroup())
		// dispatch(getBranches(false))
		dispatch(getSDCounties())
	}, [])

	useEffect((): any => {
		socket.emit(ROOM_EVENTS.JOIN_ROOM, SOCKET_ROOMS.LOCATIONS)
		socket.on(LOCATION_SOCKET_EVENTS.ADDED, res => dispatch(saveLocationBySockets({ ...res })))
		socket.on(LOCATION_SOCKET_EVENTS.UPDATED, res => dispatch(saveLocationBySockets({ ...res })))

		return () => socket.emit(ROOM_EVENTS.LEAVE_ROOM, SOCKET_ROOMS.LOCATIONS)
	}, [])
	const options = useMemo(() => {
		return [
			{
				label: "Detail",
				perform: Permissions.VIEW_LOCATION_DETAILS,
				featureName: FeatureFlagEnums.LOCATIONS_DETAILS,
				component: <LocationDetails />,
				value: LocationTabs.Detail,
			},
			{
				label: "Escalations",
				perform: Permissions.VIEW_LOCATION_ESCALATION,
				featureName: FeatureFlagEnums.LOCATIONS_ESCALATIONS,
				component: <Escalations />,
				value: LocationTabs.Escalations,
			},
			{
				label: "Contracts",
				perform: Permissions.VIEW_LOCATION_CONTRACTS,
				featureName: FeatureFlagEnums.LOCATIONS_CONTRACTS,
				component: <Contracts />,
				value: LocationTabs.Contracts,
			},
			{
				label: "Posts",
				perform: Permissions.VIEW_LOCATION_POSTS,
				featureName: FeatureFlagEnums.LOCATIONS_POSTS,
				component: <Posts />,
				value: LocationTabs.Posts,
			},
			{
				label: "Qualifications",
				perform: Permissions.VIEW_LOCATION_QUALIFICATIONS,
				featureName: FeatureFlagEnums.LOCATIONS_QUALIFICATIONS,
				component: <LocationQualification />,
				value: LocationTabs.Qualifications,
			},
			{
				label: "Time Captures",
				perform: Permissions.VIEW_LOCATION_TIME_CAPTURE,
				featureName: FeatureFlagEnums.LOCATIONS_TIME_CAPTUTRES,
				component: <TimeCaptures />,
				value: LocationTabs.TimeCaptures,
			},
			{
				label: "Additional Detail",
				perform: Permissions.VIEW_LOCATION_DETAILS,
				featureName: FeatureFlagEnums.LOCATIONS_ADDITIONAL_DETAILS,
				component: <AdditionalDetail />,
				value: LocationTabs.AdditionalDetail,
			},
			{
				label: "Documents",
				perform: Permissions.VIEW_LOCATION_DETAILS,
				featureName: FeatureFlagEnums.LOCATIONS_DOCUMENTS,
				component: <Documents />,
				value: LocationTabs.Documents,
			},
			{
				label: "QR Code",
				perform: Permissions.VIEW_LOCATION_DETAILS,
				featureName: FeatureFlagEnums.LOCATIONS_QR_CODE,
				component: <QRCodeTab />,
				value: LocationTabs.QRCodes,
			},
			{
				label: "Incidents",
				perform: Permissions.VIEW_LOCATION_DETAILS,
				featureName: FeatureFlagEnums.LOCATIONS_INCIDENTS,
				component: <Incidents />,
				value: LocationTabs.Incidents,
			},
			{
				label: "Complaints",
				perform: Permissions.VIEW_LOCATION_DETAILS,
				featureName: FeatureFlagEnums.LOCATIONS_COMPLAINTS, // TODO: needs to be updated
				component: <Complaints />,
				value: LocationTabs.Complaints,
			},
		].filter(item => userHasPermissions(item.perform) && featureFlagEnabled(item.featureName))
	}, [selectedLocation])

	return (
		<>
			<LocationSidebar selected={selectedLocation} handleSelected={handleSelected} />
			<div className="sticky top-0 z-10">
				<AllowedTo perform={Permissions.VIEW_LOCATION_DETAILS}>
					<LocationTopBar />
				</AllowedTo>
				<Divider style={{ margin: "8px", marginLeft: "0px", marginRight: "0px" }} />
				<div className="flex items-center justify-between pr-5">
					<Segmented
						disabled={!selectedLocation}
						options={options as any}
						value={paramTab}
						onChange={(e: any) => setSearchParams({ tab: e })}
					/>
					<AntdButton
						onClick={() => {
							//dispatch(setSelectedLocation(""))
							selectLocation("new", LocationTabs.Detail)
							//setSearchParams({ tab: LocationTabs.Detail })
						}}
					>
						Add Site
					</AntdButton>
				</div>
				<Divider style={{ margin: "8px", marginLeft: "0px", marginRight: "0px" }} />
			</div>
			<div id="location-segment-body" className="overflow-y-auto px-2">
				{options?.find(item => item.value === paramTab)?.component}
			</div>
		</>
	)
}

export default Index
